<template>
  <div v-if="gameVisible" class="game-container" :style="overlayStyle">
    <div v-if="showTutorial" class="overlay tutorial-overlay">
      <v-card
        class="result-card pa-6 text-center"
        :style="modalBgStyle"
        width="90%"
        max-width="400"
      >
        <h2 class="white--text mb-4 mt-2">{{ tutorialTitle }}</h2>
        <div class="tutorial-content white--text mb-6">
          <div
            class="tutorial-row"
            v-for="(step, index) in tutorialSteps"
            :key="index"
          >
            <img v-if="playerImg" :src="playerImg.src" class="icon-small" />
            <p>{{ step }}</p>
          </div>
        </div>
        <button
          class="custom-btn btn-glass-blue pulse-blue"
          @click="startAfterTutorial"
        >
          {{ tutorialBtn }}
        </button>
      </v-card>
    </div>

    <div class="bubbles-container">
      <div
        v-for="i in 10"
        :key="i"
        class="bubble"
        :style="`left: ${i * 10}%; animation-delay: ${i * 0.5}s`"
      ></div>
    </div>

    <div v-if="state === 'playing' || state === 'paused'" class="hud-container">
      <div class="game-controls">
        <div class="control-btn" @click="togglePause">
          <v-icon color="white">{{
            state === "paused" ? "mdi-play" : "mdi-pause"
          }}</v-icon>
        </div>

        <div class="hud-item-wood">
          <span>❤️ {{ lives }}</span>
          <div class="divider"></div>
          <span class="gold-text">{{ scoreLabel }}: {{ score }}</span>
        </div>
        <div class="control-btn" @click="toggleSound">
          <v-icon color="white">{{
            soundEnabled ? "mdi-volume-high" : "mdi-volume-off"
          }}</v-icon>
        </div>
      </div>
    </div>

    <canvas
      ref="gameCanvas"
      class="game-canvas"
      @mousedown="handleStart"
      @mousemove="handleMove"
      @mouseup="handleEnd"
      @touchstart.prevent="onTouchStart"
      @touchmove.prevent="handleMove"
      @touchend="handleEnd"
    >
    </canvas>

    <div
      v-if="turboReady && state === 'playing'"
      class="turbo-btn"
      @click="activateTurbo"
    >
      <img v-if="magnetImg" :src="magnetImg" alt="Turbo" class="turbo-icon" />
      <div class="turbo-label">{{ magnetLabel }}</div>
    </div>

    <div
      v-if="
        (state !== 'playing' && state !== 'paused' && !showTutorial) ||
        showInternalForm
      "
      class="overlay"
    >
      <v-card
        class="result-card pa-6 text-center"
        :style="modalBgStyle"
        width="92%"
        max-width="400"
      >
        <div v-if="state === 'start'">
          <h1 class="white--text mb-8 mt-4">{{ gameTitle }}</h1>
          <button
            class="custom-btn btn-glass-blue pulse-blue mb-4"
            @click="initGame"
          >
            {{ startBtn }}
          </button>
        </div>

        <div v-else-if="!showInternalForm">
          <h2 class="white--text mb-2 mt-4">
            {{ lives <= 0 ? failMsg : winMsg }}
          </h2>
          <div class="final-score mb-4">{{ score }}</div>
          <button
            v-if="shouldShowSubmitForm"
            class="custom-btn btn-glass-blue pulse-blue mb-4"
            @click="showInternalForm = true"
          >
            {{ submitBtn }}
          </button>
          <button
            class="custom-btn btn-glass-green pulse-green mb-4"
            @click="initGame"
          >
            {{ restartBtn }}
          </button>
        </div>

        <div v-else class="phone-form">
          <h3 class="white--text mb-6 mt-4">{{ phoneTitle }}</h3>
          <v-text-field
            v-model="phoneNumber"
            solo
            light
            rounded
            maxlength="11"
            :placeholder="phonePlaceholder"
            :error-messages="phoneError"
            @input="validatePhone"
            type="tel"
          ></v-text-field>
          <button
            class="custom-btn btn-glass-green pulse-green mt-2"
            :disabled="!!phoneError || !phoneNumber"
            @click="submitPhoneAndRestart"
          >
            {{ confirmBtn }}
          </button>
          <v-btn
            block
            text
            color="white"
            class="mt-4"
            @click="showInternalForm = false"
            >{{ backBtn }}</v-btn
          >
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
export default {
  name: "Game",
  props: {
    value: {
      type: Object,
      default: () => ({
        type: "Game",
        count_play: 1,
        texts: {
          gameTitle: "مأموریت کیوآرلند",
          tutorialTitle: "راهنمای بازی",
          tutorialSteps: [
            "درگ برای حرکت",
            "اسکن کیوآرها",
            "دوری از موانع",
            "استفاده از آهنربا",
          ],
          tutorialBtn: "فهمیدم",
          scoreLabel: "امتیاز",
          magnetLabel: "آهنربا فعال شد!",
          winMsg: "عالی بود!",
          failMsg: "دوباره تلاش کن",
          startBtn: "شروع",
          submitBtn: "ثبت امتیاز",
          restartBtn: "تلاش مجدد",
          phoneTitle: "دریافت جایزه",
          phonePlaceholder: "شماره موبایل...",
          confirmBtn: "تایید",
          backBtn: "بازگشت",
        },
        images: {
          bgMain: "",
          bgModal: "",
          player: "",
          playerHappy: "",
          playerSad: "",
          magnet: "",
          qrs: [],
          traps: [],
          phoneFrameBackground: "",
        },
        config: {
          initialLives: 3,
          pointsPerQR: 10,
          magnetThreshold: 50,
          magnetDuration: 5000,
          spawnRateEnemy: 60,
          spawnRateQR: 90,
          minSpeed: 3,
          maxSpeed: 8,
        },
        login: {
          requirePhoneNumber: true,
          phoneValidationPattern: "^09\\d{9}$",
          enableEmail: false,
          enableSocialLogin: false,
        },
      }),
    },
  },
  data() {
    return {
      gameVisible: true,
      showTutorial: true,
      state: "idle",
      score: 0,
      lives: this.value.config.initialLives || 3,
      soundEnabled: true,
      showInternalForm: false,
      phoneNumber: "",
      phoneError: "",
      canvas: null,
      ctx: null,
      animationId: null,
      frameCount: 0,
      player: { x: 0, y: 0, width: 70, height: 90 },
      enemies: [],
      powerups: [],
      particles: [],
      floatingTexts: [],
      playerImg: null,
      playerHappyImg: null,
      playerSadImg: null,
      playerFace: "normal",
      magnetImg: null,
      powerupImgs: [], // این همان لیست تصاویر QR است
      enemyImgs: [], // این همان لیست تصاویر موانع است
      shake: 0,
      isDragging: false,
      turboReady: false,
      turboActive: false,
      lastTurboScore: 0,
    };
  },
  computed: {
    gameTitle() {
      return this.value.texts.gameTitle || "مأموریت کیوآرلند";
    },
    tutorialTitle() {
      return this.value.texts.tutorialTitle || "راهنمای بازی";
    },
    tutorialSteps() {
      return (
        this.value.texts.tutorialSteps || [
          "درگ برای حرکت",
          "اسکن کیوآرها",
          "دوری از موانع",
          "استفاده از آهنربا",
        ]
      );
    },
    tutorialBtn() {
      return this.value.texts.tutorialBtn || "فهمیدم";
    },
    scoreLabel() {
      return this.value.texts.scoreLabel || "امتیاز";
    },
    magnetLabel() {
      return this.value.texts.magnetLabel || "آهنربا فعال شد!";
    },
    winMsg() {
      return this.value.texts.winMsg || "عالی بود!";
    },
    failMsg() {
      return this.value.texts.failMsg || "دوباره تلاش کن";
    },
    startBtn() {
      return this.value.texts.startBtn || "شروع";
    },
    submitBtn() {
      return this.value.texts.submitBtn || "ثبت امتیاز";
    },
    restartBtn() {
      return this.value.texts.restartBtn || "تلاش مجدد";
    },
    phoneTitle() {
      return this.value.texts.phoneTitle || "دریافت جایزه";
    },
    phonePlaceholder() {
      return this.value.texts.phonePlaceholder || "شماره موبایل...";
    },
    confirmBtn() {
      return this.value.texts.confirmBtn || "تایید";
    },
    backBtn() {
      return this.value.texts.backBtn || "بازگشت";
    },
    initialLives() {
      return this.value.config.initialLives || 3;
    },
    pointsPerQR() {
      return this.value.config.pointsPerQR || 10;
    },
    magnetThreshold() {
      return this.value.config.magnetThreshold || 50;
    },
    magnetDuration() {
      return this.value.config.magnetDuration || 5000;
    },
    spawnRateEnemy() {
      return this.value.config.spawnRateEnemy || 60;
    },
    spawnRateQR() {
      return this.value.config.spawnRateQR || 90;
    },
    minSpeed() {
      return this.value.config.minSpeed || 3;
    },
    maxSpeed() {
      return this.value.config.maxSpeed || 8;
    },
    requirePhoneNumber() {
      return this.value.login.requirePhoneNumber !== undefined
        ? this.value.login.requirePhoneNumber
        : true;
    },
    phoneValidationPattern() {
      return this.value.login.phoneValidationPattern || "^09\\d{9}$";
    },
    shouldShowSubmitForm() {
      return this.requirePhoneNumber;
    },
    overlayStyle() {
      const bgMain = this.value.images.bgMain;
      return {
        backgroundImage: bgMain
          ? `url(${bgMain})`
          : `url(${require("@/assets/images/game/background_qr2.webp")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    },
    modalBgStyle() {
      const bgModal = this.value.images.bgModal;
      return {
        backgroundImage: bgModal
          ? `url(${bgModal})`
          : `url(${require("@/assets/images/game/backgroundQr.webp")})`,
        backgroundSize: "cover",
        borderRadius: "20px",
        border: "3px solid #ffd700",
        position: "relative",
      };
    },
  },
  mounted() {
    this.loadAssets().then(() => {
      if (this.gameVisible && !this.showTutorial) this.initCanvas();
    });
  },
  methods: {
    playSound(type) {
      if (!this.soundEnabled) return;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      if (type === "score") {
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(800, audioCtx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(
          1200,
          audioCtx.currentTime + 0.1,
        );
        gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
        gainNode.gain.fadeOut = gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioCtx.currentTime + 0.2,
        );
      } else if (type === "hit") {
        oscillator.type = "sawtooth";
        oscillator.frequency.setValueAtTime(150, audioCtx.currentTime);
        oscillator.frequency.linearRampToValueAtTime(
          50,
          audioCtx.currentTime + 0.3,
        );
        gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioCtx.currentTime + 0.3,
        );
      }

      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.3);
    },
    triggerVibrate(duration = 100) {
      if ("vibrate" in navigator) {
        navigator.vibrate(duration);
      }
    },
    async loadAssets() {
      const load = (src) =>
        new Promise((res) => {
          const img = new Image();
          img.crossOrigin = "anonymous"; // Handle CORS issues
          img.onload = () => res(img);
          img.onerror = () => res(null);
          img.src = src;
        });

      // Load player images
      if (this.value.images.player) {
        this.playerImg = await load(this.value.images.player);
      } else {
        this.playerImg = await load(require("@/assets/images/game/phone.webp"));
      }

      if (this.value.images.playerHappy) {
        this.playerHappyImg = await load(this.value.images.playerHappy);
      } else {
        this.playerHappyImg = await load(
          require("@/assets/images/game/phonehappy.webp"),
        );
      }

      if (this.value.images.playerSad) {
        this.playerSadImg = await load(this.value.images.playerSad);
      } else {
        this.playerSadImg = await load(
          require("@/assets/images/game/phonesad.webp"),
        );
      }

      // Load magnet image
      if (this.value.images.magnet) {
        this.magnetImg = await load(this.value.images.magnet);
      }

      // Clear previous images
      this.powerupImgs = [];
      this.enemyImgs = [];

      // Load QR images (powerups)
      const qrs =
        this.value.images.qrs && this.value.images.qrs.length > 0
          ? this.value.images.qrs
          : [
              require("@/assets/images/game/qr17.webp"),
              require("@/assets/images/game/qrland.webp"),
              require("@/assets/images/game/qr.webp"),
            ];

      for (let src of qrs) {
        let img = await load(src);
        if (img) this.powerupImgs.push(img);
      }

      // Load trap images (enemies)
      const traps =
        this.value.images.traps && this.value.images.traps.length > 0
          ? this.value.images.traps
          : [
              require("@/assets/images/game/trip(6).webp"),
              require("@/assets/images/game/trip(7).webp"),
              require("@/assets/images/game/powder-turbo2.webp"),
            ];

      for (let src of traps) {
        let img = await load(src);
        if (img) this.enemyImgs.push(img);
      }
    },
    initCanvas() {
      this.$nextTick(() => {
        const canvas = this.$refs.gameCanvas;
        if (!canvas) return;
        this.canvas = canvas;
        this.ctx = canvas.getContext("2d");
        this.resize();
        window.addEventListener("resize", this.resize);
      });
    },
    resize() {
      if (!this.canvas) return;
      this.canvas.width = this.canvas.parentElement.clientWidth;
      this.canvas.height = this.canvas.parentElement.clientHeight;
      this.player.y = this.canvas.height - 130;
      this.player.x = this.canvas.width / 2 - 35;
    },
    startAfterTutorial() {
      this.showTutorial = false;
      this.state = "start";
      this.initCanvas();
    },
    initGame() {
      cancelAnimationFrame(this.animationId);
      this.state = "playing";
      this.score = 0;
      this.lives = this.initialLives;
      this.frameCount = 0;
      this.enemies = [];
      this.powerups = [];
      this.particles = [];
      this.floatingTexts = [];
      this.turboReady = false;
      this.lastTurboScore = 0;
      this.showInternalForm = false;
      this.animate();
    },
    spawn(list, imgs, size) {
      if (!imgs.length || !this.canvas) return;
      list.push({
        x: Math.random() * (this.canvas.width - size),
        y: -size,
        size,
        speed: 3 + Math.random() * 4,
        img: imgs[Math.floor(Math.random() * imgs.length)],
      });
    },
    createParticles(x, y, color) {
      for (let i = 0; i < 10; i++) {
        this.particles.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 5,
          vy: (Math.random() - 0.5) * 5,
          life: 1.0,
          color,
        });
      }
    },

    update() {
      if (this.state !== "playing") return;
      this.frameCount++;

      // منطق توربو
      if (
        this.score > 0 &&
        this.score % this.magnetThreshold === 0 &&
        this.score !== this.lastTurboScore
      ) {
        this.turboReady = true;
        this.lastTurboScore = this.score;
      }

      if (this.frameCount % this.spawnRateEnemy === 0)
        this.spawn(this.enemies, this.enemyImgs, 60);
      if (this.frameCount % this.spawnRateQR === 0)
        this.spawn(this.powerups, this.powerupImgs, 45);

      // آپدیت موانع
      for (let i = this.enemies.length - 1; i >= 0; i--) {
        let e = this.enemies[i];
        e.y += e.speed;
        if (this.checkCollision(this.player, e)) {
          this.lives--;
          this.shake = 10;
          this.playSound("hit");
          this.triggerVibrate(200);
          this.playerFace = "sad";
          setTimeout(() => {
            if (this.playerFace === "sad") this.playerFace = "normal";
          }, 500);
          this.createParticles(e.x + e.size / 2, e.y + e.size / 2, "#ff0000");
          this.enemies.splice(i, 1);
          if (this.lives <= 0) this.state = "gameover";
        } else if (e.y > this.canvas.height) this.enemies.splice(i, 1);
      }

      for (let i = this.powerups.length - 1; i >= 0; i--) {
        let p = this.powerups[i];
        if (this.turboActive) {
          p.x += (this.player.x - p.x) * 0.15;
          p.y += (this.player.y - p.y) * 0.15;
        } else {
          p.y += p.speed;
        }

        if (this.checkCollision(this.player, p)) {
          this.score += this.pointsPerQR;
          this.playSound("score");
          this.triggerVibrate(50);
          this.playerFace = "happy";
          setTimeout(() => {
            if (this.playerFace === "happy") this.playerFace = "normal";
          }, 500);
          this.floatingTexts.push({
            x: this.player.x + this.player.width / 2,
            y: this.player.y,
            life: 1.0,
          });
          this.createParticles(p.x + p.size / 2, p.y + p.size / 2, "#ffd700"); // ذرات طلایی برای امتیاز
          this.powerups.splice(i, 1);
        } else if (p.y > this.canvas.height) this.powerups.splice(i, 1);
      }

      // آپدیت ذرات
      for (let i = this.particles.length - 1; i >= 0; i--) {
        let p = this.particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.02;
        if (p.life <= 0) this.particles.splice(i, 1);
      }

      // آپدیت متن‌های شناور
      for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
        let t = this.floatingTexts[i];
        t.y -= 2; // حرکت به سمت بالا
        t.life -= 0.02;
        if (t.life <= 0) this.floatingTexts.splice(i, 1);
      }
    },

    draw() {
      if (!this.ctx || !this.canvas) return;
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.save();
      let currentImg = this.playerImg;
      if (this.playerFace === "happy") currentImg = this.playerHappyImg;
      if (this.playerFace === "sad") currentImg = this.playerSadImg;

      if (currentImg) {
        this.ctx.drawImage(
          currentImg,
          this.player.x,
          this.player.y,
          this.player.width,
          this.player.height,
        );
      }
      if (this.shake > 0) {
        this.ctx.translate(Math.random() * 10 - 5, Math.random() * 10 - 5);
        this.shake--;
      }

      // رسم دشمنان و امتیازها
      this.enemies.forEach((e) =>
        this.ctx.drawImage(e.img, e.x, e.y, e.size, e.size),
      );
      this.powerups.forEach((p) =>
        this.ctx.drawImage(p.img, p.x, p.y, p.size, p.size),
      );

      // رسم ذرات (Particles)
      this.particles.forEach((p) => {
        this.ctx.globalAlpha = p.life;
        this.ctx.fillStyle = p.color;
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        this.ctx.fill();
      });

      this.ctx.globalAlpha = 1;
      this.floatingTexts.forEach((t) => {
        this.ctx.fillStyle = `rgba(119, 90, 22, ${t.life})`;
        this.ctx.font = "bold 26px Arial";
        this.ctx.fillText(`+${this.pointsPerQR}`, t.x, t.y);
      });

      this.ctx.restore();
    },
    animate() {
      if (this.state === "playing") {
        this.update();
        this.draw();
        this.animationId = requestAnimationFrame(this.animate);
      }
    },
    checkCollision(p, o) {
      return (
        p.x < o.x + o.size - 15 &&
        p.x + p.width > o.x + 15 &&
        p.y < o.y + o.size - 15 &&
        p.y + p.height > o.y + 15
      );
    },
    handleMove(e) {
      if (!this.isDragging || this.state !== "playing") return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const rect = this.canvas.getBoundingClientRect();
      let newX = clientX - rect.left - this.player.width / 2;
      this.player.x = Math.max(
        0,
        Math.min(newX, this.canvas.width - this.player.width),
      );
    },
    handleStart() {
      this.isDragging = true;
    },
    handleEnd() {
      this.isDragging = false;
    },
    onTouchStart(e) {
      this.handleStart();
    },
    togglePause() {
      this.state = this.state === "playing" ? "paused" : "playing";
      if (this.state === "playing") this.animate();
    },
    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
    },
    activateTurbo() {
      this.turboActive = true;
      this.turboReady = false;
      setTimeout(() => {
        this.turboActive = false;
      }, this.magnetDuration);
    },
    validatePhone() {
      this.phoneNumber = this.phoneNumber.replace(/[^\d]/g, "");
      this.phoneError = new RegExp(this.phoneValidationPattern).test(
        this.phoneNumber,
      )
        ? ""
        : "شماره نامعتبر است";
    },
    submitPhoneAndRestart() {
      if (this.phoneError || !this.phoneNumber) return;
      // Emit event to parent component with score and phone number
      this.$emit("submit-score", {
        score: this.score,
        phoneNumber: this.phoneNumber,
      });
      this.phoneNumber = "";
      this.showInternalForm = false;
      this.initGame();
    },
    closeGame() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.game-container {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  max-height: 100%;
  box-sizing: border-box;
  flex: 1;
}
.game-canvas {
  display: block;
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 5;
  flex: 1;
  max-width: 100%;
  max-height: 100%;
  min-height: 0;
}
.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 50;
}
.tutorial-row {
  display: flex;
  align-items: center;
  background: rgba(31, 21, 21, 0.97);
  border-radius: 16px;
  padding: 8px;
  margin-bottom: 10px;
  border: 2px solid goldenrod;
  direction: rtl;
}
.icon-small {
  width: 30px;
  height: 30px;
  margin-left: 10px;
  object-fit: contain;
}
.tutorial-row p {
  margin: 0;
  font-size: 0.9rem;
  color: white;
}
.custom-btn {
  width: 100%;
  padding: 12px;
  border-radius: 50px;
  border: none;
  font-weight: bold;
  color: white;
  cursor: pointer;
  margin-top: 10px;
}
.btn-glass-blue {
  background: linear-gradient(180deg, #4facfe 0%, #00f2fe 100%);
}
.btn-glass-green {
  background: linear-gradient(180deg, #43e97b 0%, #38f9d7 100%);
}
.hud-container {
  position: absolute;
  top: 0px;
  padding-top: 15px;
  padding-bottom: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 20;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
}
.hud-item-wood {
  background: rgba(0, 0, 0, 0.7);
  padding: 8px 20px;
  border-radius: 30px;
  color: white;
  border: 2px solid goldenrod;
  display: flex;
  gap: 15px;
}
.game-controls {
  display: flex;
  gap: 10px;
  margin-top: 5px;
  pointer-events: auto;
}
.control-btn {
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid goldenrod;
}
.control-btn v-icon {
  color: goldenrod;
}
.turbo-btn {
  position: absolute;
  bottom: 80px;
  right: 20px;
  z-index: 100;
  text-align: center;
  cursor: pointer;
}
.turbo-icon {
  width: 60px;
  height: 60px;
  filter: drop-shadow(0 0 10px gold);
}
.turbo-label {
  color: goldenrod;
  font-size: 12px;
  font-weight: bold;
}
.final-score {
  font-size: 4rem;
  color: rgb(218, 165, 38);
  text-shadow: 2px 2px black;
}
</style>
